import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Radar, MapPin, X } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const PREF = { one_on_one: "1:1", group: "Group" };

const ago = (iso) => {
  const m = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  return h < 24 ? `${h}h ago` : `${Math.round(h / 24)}d ago`;
};

export const PlayerInterestFeed = () => {
  const [items, setItems] = useState([]);
  const [pending, setPending] = useState(null);
  useEffect(() => { api.get("/coach/player-interest").then((r) => setItems(r.data?.items || [])).catch(() => setItems([])); }, []);
  if (!items.length) return null;
  const dismiss = async () => {
    const id = pending;
    setPending(null);
    try {
      await api.post(`/coach/player-interest/${id}/dismiss`);
      setItems((cur) => cur.filter((x) => x.id !== id));
    } catch { toast.error("Couldn't dismiss that entry"); }
  };
  return (
    <section className="card-surface p-5 sm:p-6 border-l-4 fade-up" style={{ borderLeftColor: "#CCFF00" }} data-testid="player-interest-section">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
        <h2 className="text-base md:text-lg font-bold tracking-tight inline-flex items-center gap-2"><Radar className="w-4 h-4 text-[#CCFF00]" /> Player interest</h2>
        <span className="text-xs text-[#A3A3A3]" data-testid="player-interest-count">FYI · last 7 days · {items.length}</span>
      </div>
      <ul className="divide-y divide-[#1F1F1F]">
        {items.map((it) => (
          <li key={it.id} className="flex items-center gap-3 py-3" data-testid={`player-interest-item-${it.id}`}>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">{it.player_name}</div>
              <div className="text-xs text-[#A3A3A3] truncate inline-flex items-center gap-1">
                <MapPin className="w-3 h-3" />{it.location}{it.note ? ` · “${it.note}”` : ""}
              </div>
            </div>
            <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border border-[#CCFF00]/50 text-[#CCFF00] shrink-0">{PREF[it.preference]}</span>
            <span className="text-xs text-[#737373] shrink-0 w-14 text-right">{ago(it.created_at)}</span>
            <button type="button" onClick={() => setPending(it.id)} aria-label="Dismiss" data-testid={`player-interest-dismiss-${it.id}`}
              className="p-1 text-[#737373] hover:text-white transition-colors shrink-0"><X className="w-4 h-4" /></button>
          </li>
        ))}
      </ul>
      <Dialog open={!!pending} onOpenChange={(o) => { if (!o) setPending(null); }}>
        <DialogContent className="bg-[#0F0F0F] border border-[#262626] max-w-sm text-[#E5E5E5]" data-testid="player-interest-dismiss-dialog">
          <DialogHeader>
            <DialogTitle>Remove entry</DialogTitle>
            <DialogDescription className="text-sm text-[#A3A3A3] pt-1">Remove this entry from your feed? This can't be undone.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setPending(null)} data-testid="player-interest-dismiss-cancel"
              className="px-4 py-2 text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-white">Cancel</button>
            <button type="button" onClick={dismiss} data-testid="player-interest-dismiss-confirm"
              className="min-h-[40px] px-4 py-2 text-xs uppercase tracking-wider font-bold bg-[#CCFF00] text-black">Confirm</button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};
